import React, { useState, useCallback, useEffect } from "react";
import { Button, Box, Typography, Chip, Grid } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Add } from "@material-ui/icons";

import { useApi } from "../../../../useApi";
import { useAuth } from "../../../../../features/auth/context";
import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SitePhotoCategoryCounts, PHOTO_CATEGORY_LABELS, PHOTO_CATEGORIES } from "../../../../../dtos/sitePhoto";

import { PhotoUpload } from "./PhotoUpload";
import { PhotoGallery } from "./PhotoGallery";

const useStyles = makeStyles((theme: Theme) => ({
    sectionContainer: {
        marginTop: "40px",
    },
    sectionHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "24px",
        paddingBottom: "16px",
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    headerTitle: {
        display: "flex",
        alignItems: "center",
        color: theme.palette.text.primary,
        margin: "40px 0 40px 0",
    },
    headerIcon: {
        marginRight: "12px",
        fontSize: "1.8rem",
    },
    categorySummary: {
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
    },
    categoryChip: {
        margin: "4px 8px 4px 0",
    },
    totalCount: {
        marginBottom: "12px",
        fontWeight: 500,
        color: theme.palette.text.secondary,
    }
}));

interface Props {
    siteId: number;
}

export const SitePhotos: React.FC<Props> = ({ siteId }) => {
    const classes = useStyles();
    const { sitePhotoApi } = useApi();
    const { isSiteEmployee, isGlobalAdmin, isDealerAdmin, isSiteAdmin, isOrgAdmin } = useAuth();

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [categoryCounts, setCategoryCounts] = useState<SitePhotoCategoryCounts>({});
    const [refreshKey, setRefreshKey] = useState(0);

    const canUpload = isSiteEmployee || isGlobalAdmin || isDealerAdmin || isSiteAdmin || isOrgAdmin;

    const loadCategoryCounts = useCallback(async () => {
        try {
            const result = await sitePhotoApi.getPhotoCategoryCounts(siteId);
            if (result.result === AjaxResult.Success && result.data) {
                setCategoryCounts(result.data);
                return;
            }
        } catch (error) {
            console.error("Error loading photo category counts:", error);
        }

        try {
            const listResult = await sitePhotoApi.listSitePhotos(siteId);
            if (listResult.result === AjaxResult.Success && listResult.data) {
                const initialCounts: SitePhotoCategoryCounts = PHOTO_CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: 0 }), {} as SitePhotoCategoryCounts);
                const counts = listResult.data.reduce((acc: SitePhotoCategoryCounts, p: any) => {
                    const cat = (p.category as string) || "uncategorized";
                    acc[cat] = (acc[cat] || 0) + 1;
                    return acc;
                }, initialCounts);
                setCategoryCounts(counts);
            }
        } catch (error) {
            console.error("Error loading photos for category counts:", error);
        }
    }, [siteId, sitePhotoApi]);

    useEffect(() => {
        loadCategoryCounts();
    }, [loadCategoryCounts, refreshKey]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const totalPhotos = Object.values(categoryCounts).reduce((sum, count) => sum + count, 0);

    return (
        <div className={classes.sectionContainer}>
            <div className={classes.sectionHeader}>
                <h1 className={classes.headerTitle}>Images</h1>
                {canUpload && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setUploadDialogOpen(true)}
                        size="large"
                    >
                        <Add style={{ marginRight: "8px" }} />
                        Upload Photo
                    </Button>
                )}
            </div>

            {totalPhotos > 0 && (
                <Box className={classes.categorySummary}>
                    <Typography variant="body1" className={classes.totalCount}>
                        Total: {totalPhotos} photo{totalPhotos !== 1 ? "s" : ""}
                    </Typography>
                    <Grid container spacing={1}>
                        {Object.entries(categoryCounts).map(([category, count]) => (
                            <Grid item key={category}>
                                <Chip
                                    label={`${PHOTO_CATEGORY_LABELS[category as keyof typeof PHOTO_CATEGORY_LABELS] || category}: ${count}`}
                                    size="medium"
                                    className={classes.categoryChip}
                                    color={count > 0 ? "primary" : "default"}
                                    variant={count > 0 ? "default" : "outlined"}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            <PhotoGallery
                siteId={siteId}
                key={refreshKey}
                onRefresh={handleRefresh}
            />

            {canUpload && (
                <PhotoUpload
                    siteId={siteId}
                    open={uploadDialogOpen}
                    onClose={() => setUploadDialogOpen(false)}
                    onUploadSuccess={handleRefresh}
                />
            )}
        </div>
    );
}; 